import React, { useState, useEffect, useCallback } from 'react';
import { 
  AlertTriangle, Plus, Search, Grid, List, Filter, X
} from 'lucide-react';
import { issuesApi } from '../../services/issuesApi';
import IssueCard from './IssueCard';
import './Issues.css';

const CATEGORIES = [
  'Engine', 'Electronics', 'Hull/Gel Coat', 'Trailer', 
  'Tower/Bimini', 'Ballast System', 'Stereo/Audio', 
  'Driving / Surf Systems', 'Warranty', 'Customer Service', 'Other'
];

const BOAT_MODELS = [
  'S220', 'S240', 'Nv213', 'Nv233', 'Nv243', 'Fe22', 'Fe23', 'Fe25', 'Ri230', 'Ri245', 'Ri265'
];

const STATUSES = ['Open', 'In Progress', 'Under Review', 'Resolved', 'Closed'];

export default function IssuesDashboard({ onNavigate, onSelectIssue }) {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  
  // Navigation helper
  const navigate = (path) => {
    if (onNavigate) {
      if (path === '/issues/submit') {
        onNavigate('issues-submit');
      } else if (path.startsWith('/issues/')) {
        const issueId = path.replace('/issues/', '');
        if (onSelectIssue) onSelectIssue(issueId);
        onNavigate('issues-detail');
      } else if (path === '/issues') {
        onNavigate('issues');
      }
    }
  };
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  
  // View
  const [viewMode, setViewMode] = useState('grid');
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false);

  const loadIssues = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {
        search: searchTerm,
        status: selectedStatuses.join(','),
        category: selectedCategories.join(','),
        model: selectedModels.join(','),
        sortBy
      };
      
      const data = await issuesApi.getIssues(filters);
      setIssues(data.issues || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Error loading issues:', err);
      setError('Failed to load issues. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedStatuses, selectedCategories, selectedModels, sortBy]);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadIssues();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, loadIssues]);

  const handleStatusToggle = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleModelToggle = (model) => {
    setSelectedModels(prev =>
      prev.includes(model)
        ? prev.filter(m => m !== model)
        : [...prev, model]
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedStatuses([]);
    setSelectedCategories([]);
    setSelectedModels([]);
    setSortBy('newest');
  };

  const handleIssueClick = (issue) => {
    navigate(`/issues/${issue.id}`);
  };

  const hasActiveFilters = searchTerm || selectedStatuses.length || 
    selectedCategories.length || selectedModels.length;

  return (
    <div className="issues-page">
      {/* Header */}
      <div className="issues-header">
        <h1>
          <AlertTriangle size={28} />
          Issue & Complaint Tracker
        </h1>
        <button 
          className="submit-issue-btn"
          onClick={() => navigate('/issues/submit')}
        >
          <Plus size={20} />
          Submit New Issue
        </button>
      </div>

      <div className="issues-dashboard">
        {/* Filter Overlay for Mobile */}
        <div 
          className={`filter-overlay ${filterSidebarOpen ? 'open' : ''}`}
          onClick={() => setFilterSidebarOpen(false)}
        />

        {/* Filter Sidebar */}
        <aside className={`filter-sidebar ${filterSidebarOpen ? 'open' : ''}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Filters</h2>
            <button 
              onClick={() => setFilterSidebarOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'none' }}
              className="mobile-close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Status Filter */}
          <div className="filter-section">
            <h3>Status</h3>
            {STATUSES.map(status => (
              <label key={status} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedStatuses.includes(status)}
                  onChange={() => handleStatusToggle(status)}
                />
                <span>{status}</span>
              </label>
            ))}
          </div>

          {/* Category Filter */}
          <div className="filter-section">
            <h3>Category</h3>
            {CATEGORIES.map(category => (
              <label key={category} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(category)}
                  onChange={() => handleCategoryToggle(category)}
                />
                <span>{category}</span>
              </label>
            ))}
          </div>

          {/* Model Filter */}
          <div className="filter-section">
            <h3>Boat Model</h3>
            {BOAT_MODELS.map(model => (
              <label key={model} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedModels.includes(model)}
                  onChange={() => handleModelToggle(model)}
                />
                <span>{model}</span>
              </label>
            ))}
          </div>

          {/* Sort */}
          <div className="filter-section">
            <h3>Sort By</h3>
            <select 
              className="filter-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Most Recent</option>
              <option value="oldest">Oldest First</option>
              <option value="upvotes">Most Upvoted</option>
              <option value="comments">Most Comments</option>
            </select>
          </div>

          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearFilters}>
              Clear All Filters
            </button>
          )}
        </aside>

        {/* Main Content */}
        <main className="issues-content">
          {/* Search Bar */}
          <div className="search-bar">
            <div className="search-input-wrapper">
              <Search size={20} />
              <input
                type="text"
                className="search-input"
                placeholder="Search issues by title or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Results Info */}
          <div className="results-info">
            <span className="results-count">
              Showing {issues.length} of {total} issues
            </span>
            <div className="view-toggle">
              <button 
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
              >
                <Grid size={16} />
                Grid
              </button>
              <button 
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
              >
                <List size={16} />
                List
              </button>
            </div>
          </div>

          {/* Issues Grid/List */}
          {loading ? (
            <div className="empty-state">
              <div className="loading-spinner-small" style={{ margin: '0 auto' }} />
              <p>Loading issues...</p>
            </div>
          ) : error ? (
            <div className="empty-state">
              <AlertTriangle size={60} />
              <h3>Error Loading Issues</h3>
              <p>{error}</p>
              <button className="btn-primary" onClick={loadIssues} style={{ marginTop: '1rem' }}>
                Try Again
              </button>
            </div>
          ) : issues.length === 0 ? (
            <div className="empty-state">
              <AlertTriangle size={60} />
              <h3>No Issues Found</h3>
              <p>
                {hasActiveFilters 
                  ? 'Try adjusting your filters or search term.'
                  : 'Be the first to submit an issue!'}
              </p>
              {!hasActiveFilters && (
                <button 
                  className="btn-primary" 
                  onClick={() => navigate('/issues/submit')}
                  style={{ marginTop: '1rem' }}
                >
                  Submit an Issue
                </button>
              )}
            </div>
          ) : (
            <div className={viewMode === 'grid' ? 'issues-grid' : 'issues-list'}>
              {issues.map(issue => (
                <IssueCard 
                  key={issue.id} 
                  issue={issue} 
                  onClick={handleIssueClick}
                />
              ))}
            </div>
          )}
        </main>

        {/* Mobile Filter Toggle */}
        <button 
          className="mobile-filter-toggle"
          onClick={() => setFilterSidebarOpen(true)}
        >
          <Filter size={24} />
        </button>
      </div>
    </div>
  );
}

